import { c as _c } from "react/compiler-runtime";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { generateForbiddenPageRoute, useAppParams } from "@router/router-helper";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { fetchingSdbCompanyFlagsSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { requiredRolesCompanyLevel } from "@utils/access-control/company/company-access-control";
import { requiredFeaturesCompanyLevel } from "@utils/feature-control/company/company-feature-control-helper";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { requiredPermissionsProjectLevel } from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { Navigate } from "react-router-dom";
/**
 * Returns a Node module that returns to the forbidden page,
 * including information in the url like the required missing roles.
 *
 * @returns A Node module that once instantiated redirects to forbidden page.
 */
function redirectToForbiddenRoute({
  companyId,
  requiredCompanyRoles,
  requiredPermissions,
  requiredCompanySubscriptions,
  userId
}) {
  const forbiddenRoute = generateForbiddenPageRoute({
    companyId,
    requiredCompanyRoles,
    requiredPermissions,
    requiredCompanySubscriptions,
    userId
  });
  return <Navigate to={forbiddenRoute} />;
}

/**
 * Defines a component to secure a route based on the user company role.
 * The required company role defines an array of company roles that the
 * user should have one of them, and if it doesn't match that criteria
 * it is redirected to a forbidden page.
 * This component can be extended to add more checks like project role
 * user permissions or company features.
 */
export function SecuredRoute(t0) {
  const $ = _c(18);
  const {
    children,
    requiredRoleCompanyLevel,
    requiredFeatureCompanyLevel,
    requiredPermissionProjectLevel,
    isHidden,
    loadingContent: t1
  } = t0;
  const loadingContent = t1 === undefined ? null : t1;
  const currentUser = useAppSelector(currentUserSelector);
  const selectedProject = useAppSelector(selectedProjectSelector);
  const {
    companyId,
    projectId,
    memberId
  } = useAppParams();
  const {
    hasUserPermissionCompanyLevel
  } = useHasUserValidRoleCompanyLevel();
  const {
    hasFeatureCompanyLevel
  } = useHasUserValidFeatureCompanyLevel();
  const {
    fetchSelectedCompanyContext
  } = useAppSelector(fetchingSdbCompanyFlagsSelector);
  const {
    hasUserPermissionProjectLevel
  } = useHasUserValidPermissionProjectLevel();
  if (!currentUser || projectId && !selectedProject || fetchSelectedCompanyContext === "pending") {
    let t2;
    if ($[0] !== loadingContent) {
      t2 = <>{loadingContent}</>;
      $[0] = loadingContent;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (requiredFeatureCompanyLevel && requiredFeatureCompanyLevel in requiredFeaturesCompanyLevel && !hasFeatureCompanyLevel({
    featureName: requiredFeatureCompanyLevel
  })) {
    const t2 = currentUser?.identity;
    let t3;
    if ($[2] !== companyId || $[3] !== t2) {
      t3 = redirectToForbiddenRoute({
        companyId,
        userId: t2
      });
      $[2] = companyId;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  if (requiredRoleCompanyLevel && requiredRoleCompanyLevel in requiredRolesCompanyLevel && !hasUserPermissionCompanyLevel({
    roleName: requiredRoleCompanyLevel,
    memberId
  })) {
    let t2;
    if ($[5] !== companyId || $[6] !== currentUser?.identity || $[7] !== requiredRoleCompanyLevel) {
      const requiredCompanyRoles = requiredRolesCompanyLevel[requiredRoleCompanyLevel].companyRoles ?? [];
      const requiredCompanySubscriptions = requiredRolesCompanyLevel[requiredRoleCompanyLevel].companySubscriptionRoles ?? [];
      t2 = redirectToForbiddenRoute({
        companyId,
        requiredCompanyRoles,
        requiredCompanySubscriptions,
        userId: currentUser?.identity
      });
      $[5] = companyId;
      $[6] = currentUser?.identity;
      $[7] = requiredRoleCompanyLevel;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  if (requiredPermissionProjectLevel && requiredPermissionProjectLevel in requiredPermissionsProjectLevel) {
    const isMembersTab = requiredPermissionProjectLevel === RequiredPermissionProjectLevelName.canViewAllProjectMembers;
    const hasProjectPermission = hasUserPermissionProjectLevel({
      permissionName: requiredPermissionProjectLevel,
      project: selectedProject
    });
    const hasCompanyPermission = hasFeatureCompanyLevel({
      featureName: RequiredFeatureCompanyLevelName.canViewCompanyMembers
    });
    const isAllowed = isMembersTab ? hasProjectPermission || hasCompanyPermission : hasProjectPermission;
    if (!isAllowed) {
      const requiredPermissions = requiredPermissionsProjectLevel[requiredPermissionProjectLevel].permissions;
      const t2 = currentUser?.identity;
      let t3;
      if ($[9] !== companyId || $[10] !== requiredPermissions || $[11] !== t2) {
        t3 = redirectToForbiddenRoute({
          companyId,
          requiredPermissions,
          userId: t2
        });
        $[9] = companyId;
        $[10] = requiredPermissions;
        $[11] = t2;
        $[12] = t3;
      } else {
        t3 = $[12];
      }
      return t3;
    }
  }
  if (isHidden) {
    const t2 = currentUser?.identity;
    let t3;
    if ($[13] !== companyId || $[14] !== t2) {
      t3 = redirectToForbiddenRoute({
        companyId,
        userId: t2
      });
      $[13] = companyId;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    return t3;
  }
  let t2;
  if ($[16] !== children) {
    t2 = <>{children}</>;
    $[16] = children;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
}